/**
 * Human Scanner - Phase 2 of Resume Reality Check
 * 
 * Simulates the 6-second human recruiter scan that happens AFTER ATS.
 * Because humans decide fast and are looking for specific things.
 * 
 * @author QuickCruit Engineering Team
 */

const log = (...args) => { if (process.env.DEBUG) console.log(...args); };

class HumanScanner {
    constructor() {
        this.redFlags = this.initializeRedFlags();
        this.positiveSignals = this.initializePositiveSignals();
        this.achievementPatterns = this.initializeAchievementPatterns();
        
        log('👁️ Human Scanner initialized - Ready for 6-second analysis');
    }

    /**
     * Simulate human recruiter 6-second scan
     * Returns 40% of total score (humans are the final filter)
     */
    async simulateHumanScan(resumeText, jobDescription, atsResults) {
        const startTime = performance.now();
        
        try {
            // Parse resume for human-readable content
            const resumeProfile = this.parseResumeForHuman(resumeText);
            const jobProfile = this.parseJobForHuman(jobDescription);
            
            log('👁️ Human scanning resume profile:', resumeProfile);
            
            // Run the 6-second human checks
            const recognitionScore = this.checkInstantRecognition(resumeProfile, jobProfile);
            const credibilityScore = this.checkCredibilityIndicators(resumeProfile, resumeText);
            const redFlagScore = this.checkRedFlagDetection(resumeProfile, resumeText);
            
            // Calculate weighted human scan score
            const humanScore = Math.round(
                (recognitionScore * 0.50) +  // 20/40 = 50% of human score
                (credibilityScore * 0.375) +  // 15/40 = 37.5% of human score
                (redFlagScore * 0.125)        // 5/40 = 12.5% of human score
            );
            
            const analysisTime = Math.round(performance.now() - startTime);
            log(`👁️ Human scan completed in ${analysisTime}ms - Score: ${humanScore}%`);
            
            return {
                humanScore: Math.min(humanScore, 95), // Cap at 95%
                breakdown: {
                    instantRecognition: recognitionScore,
                    credibilityIndicators: credibilityScore,
                    redFlagDetection: redFlagScore
                },
                details: this.generateHumanFeedback(recognitionScore, credibilityScore, redFlagScore, resumeProfile, jobProfile),
                analysisTime
            };
            
        } catch (error) {
            log('❌ Human scan failed:', error);
            return this.getFallbackHumanResults();
        }
    }

    /**
     * Phase 2A: Instant Recognition (20% of total score)
     * What recruiter sees in first 2 seconds
     */
    checkInstantRecognition(resumeProfile, jobProfile) {
        let recognitionScore = 0;
        const checks = {
            titleMatch: 0,
            skillsVisible: 0,
            experienceRelevant: 0
        };
        
        // Job title alignment (critical for instant recognition)
        const titleAlignment = this.calculateTitleAlignment(resumeProfile.currentTitle, jobProfile.targetTitle);
        checks.titleMatch = titleAlignment;
        
        // Skills immediately visible
        const skillsVisibility = this.checkSkillsVisibility(resumeProfile.topSkills, jobProfile.requiredSkills);
        checks.skillsVisible = skillsVisibility;
        
        // Experience relevance at first glance
        const experienceRelevance = this.checkExperienceRelevance(resumeProfile.recentExperience, jobProfile.industry);
        checks.experienceRelevant = experienceRelevance;
        
        // Weighted average (title is most important for instant recognition)
        recognitionScore = Math.round(
            (checks.titleMatch * 0.5) +
            (checks.skillsVisible * 0.3) +
            (checks.experienceRelevant * 0.2)
        );
        
        log(`👁️ Recognition check: Title=${checks.titleMatch}%, Skills=${checks.skillsVisible}%, Experience=${checks.experienceRelevant}%`);
        
        return Math.min(recognitionScore, 95);
    }

    /**
     * Phase 2B: Credibility Indicators (15% of total score)
     * Does this person actually know what they're doing?
     */
    checkCredibilityIndicators(resumeProfile, resumeText) {
        let credibilityScore = 0;
        const checks = {
            quantifiedAchievements: 0,
            specificImpact: 0,
            technicalDepth: 0
        };
        
        // Count quantified achievements
        const achievements = this.extractQuantifiedAchievements(resumeText);
        checks.quantifiedAchievements = Math.min((achievements.length / 3) * 100, 100); // Target: 3+ quantified achievements
        
        // Check for specific vs generic language
        const specificityScore = this.checkSpecificity(resumeText);
        checks.specificImpact = specificityScore;
        
        // Technical depth assessment
        const techDepth = this.assessTechnicalDepth(resumeProfile.skills, resumeText);
        checks.technicalDepth = techDepth;
        
        // Additional lightweight recruiter checks (no AI):
        // 1. Passive voice penalty (max -3 pts)
        const passiveHits = (resumeText.match(/\b(?:was|were|is being|be|been)\s+[a-z]+ed\b/gi) || []).length;
        const totalSentences = (resumeText.match(/[.!?]/g) || []).length || 1;
        const passiveRatio = passiveHits / totalSentences;
        const passivePenalty = Math.min(Math.round(passiveRatio * 10), 3); // up to -3

        // 2. Duplicate bullet penalty (1 pt each duplicate, max 5)
        const bulletLines = resumeText.split(/\n|\r/).filter(l => /^\s*[•*-]/.test(l.trim()));
        const uniqueBullets = new Set(bulletLines.map(l => l.trim().toLowerCase()));
        const dupPenalty = Math.min(bulletLines.length - uniqueBullets.size, 5);

        // 3. Employment gap penalty (>6 mo gap up to -5)
        const gapPenalty = this.calculateGapPenalty(resumeText);

        // Combine base credibility with penalties
        credibilityScore = Math.round(
            (checks.quantifiedAchievements * 0.5) +
            (checks.specificImpact * 0.3) +
            (checks.technicalDepth * 0.2)
        );

        credibilityScore = Math.max(credibilityScore - passivePenalty - dupPenalty - gapPenalty, 0);

        checks.passivePenalty = passivePenalty;
        checks.duplicatePenalty = dupPenalty;
        checks.gapPenalty = gapPenalty;
        
        log(`🎯 Credibility check: Achievements=${achievements.length}, Specificity=${checks.specificImpact}%, TechDepth=${checks.technicalDepth}%`);
        
        return Math.min(credibilityScore, 95);
    }

    /**
     * Phase 2C: Red Flag Detection (5% of total score)
     * What makes recruiters immediately reject
     */
    checkRedFlagDetection(resumeProfile, resumeText) {
        const flags = [];
        let redFlagPenalty = 0;
        
        // Job hopping (too many short stints)
        if (this.detectJobHopping(resumeProfile.workHistory)) {
            flags.push('Job hopping pattern detected');
            redFlagPenalty += 30;
        }
        
        // Skill inflation (claiming expertise in everything)
        if (this.detectSkillInflation(resumeProfile.skills)) {
            flags.push('Unrealistic skill claims');
            redFlagPenalty += 20;
        }
        
        // Buzzword overload
        if (this.detectBuzzwordOverload(resumeText)) {
            flags.push('Excessive buzzwords/generic language');
            redFlagPenalty += 15;
        }
        
        // Calculate score (start at 100, subtract penalties)
        const redFlagScore = Math.max(100 - redFlagPenalty, 0);
        
        if (flags.length > 0) {
            log(`🚨 Red flags detected: ${flags.join(', ')}`);
        }
        
        return redFlagScore;
    }

    /**
     * Parse resume for human-readable insights
     */
    parseResumeForHuman(resumeText) {
        return {
            currentTitle: this.extractCurrentTitle(resumeText),
            topSkills: this.extractTopSkills(resumeText),
            recentExperience: this.extractRecentExperience(resumeText),
            workHistory: this.extractWorkHistory(resumeText),
            skills: this.extractAllSkills(resumeText),
            achievements: this.extractAchievements(resumeText)
        };
    }

    /**
     * Parse job description for human expectations
     */
    parseJobForHuman(jobDescription) {
        return {
            targetTitle: this.extractTargetTitle(jobDescription),
            requiredSkills: this.extractRequiredSkills(jobDescription),
            industry: this.extractIndustry(jobDescription),
            seniorityLevel: this.extractSeniorityLevel(jobDescription)
        };
    }

    /**
     * Calculate title alignment for instant recognition
     */
    calculateTitleAlignment(currentTitle, targetTitle) {
        if (!currentTitle || !targetTitle) return 30; // Default low score
        
        const current = currentTitle.toLowerCase();
        const target = targetTitle.toLowerCase();
        
        // Exact match
        if (current === target) return 100;
        
        // Role type matches
        const roleTypes = {
            'frontend': ['frontend', 'front-end', 'ui', 'react', 'angular', 'vue'],
            'backend': ['backend', 'back-end', 'api', 'server', 'node', 'python'],
            'fullstack': ['fullstack', 'full-stack', 'full stack'],
            'data': ['data', 'analytics', 'scientist', 'analyst'],
            'mobile': ['mobile', 'ios', 'android', 'react native'],
            'devops': ['devops', 'infrastructure', 'cloud', 'sre']
        };
        
        for (const [, keywords] of Object.entries(roleTypes)) {
            const currentInType = keywords.some(keyword => current.includes(keyword));
            const targetInType = keywords.some(keyword => target.includes(keyword));
            
            if (currentInType && targetInType) return 85; // Same role type
        }
        
        // Seniority level matches
        const seniorityLevels = ['junior', 'mid', 'senior', 'lead', 'principal'];
        const currentSeniority = seniorityLevels.find(level => current.includes(level));
        const targetSeniority = seniorityLevels.find(level => target.includes(level));
        
        if (currentSeniority === targetSeniority) return 70; // Same seniority
        
        // Generic developer/engineer match
        if ((current.includes('developer') || current.includes('engineer')) && 
            (target.includes('developer') || target.includes('engineer'))) {
            return 60; // Generic match
        }
        
        return 25; // Poor alignment
    }

    /**
     * Check if skills are immediately visible
     */
    checkSkillsVisibility(topSkills, requiredSkills) {
        if (!topSkills || topSkills.length === 0) return 20;
        if (!requiredSkills || requiredSkills.length === 0) return 70;
        
        const visibleMatches = requiredSkills.filter(skill => 
            topSkills.some(topSkill => 
                topSkill.toLowerCase().includes(skill.toLowerCase()) ||
                skill.toLowerCase().includes(topSkill.toLowerCase())
            )
        ).length;
        
        const visibilityScore = (visibleMatches / Math.min(requiredSkills.length, 5)) * 100;
        return Math.min(Math.round(visibilityScore), 95);
    }

    /**
     * Extract quantified achievements
     */
    extractQuantifiedAchievements(resumeText) {
        const achievements = [];
        const quantifierPatterns = [
            /\d+%/g,                    // Percentages: 25%
            /\$[\d,]+/g,               // Money: $50,000
            /\d+[kmb]?\+?/gi,          // Numbers: 5k, 2.5M
            /\d+x/gi,                  // Multipliers: 3x faster
            /\d+:\d+/g                 // Ratios: 10:1
        ];
        
        const sentences = resumeText.split(/[.!?]+/);
        
        for (const sentence of sentences) {
            for (const pattern of quantifierPatterns) {
                if (pattern.test(sentence)) {
                    achievements.push({
                        text: sentence.trim(),
                        metrics: sentence.match(pattern)
                    });
                    break; // One achievement per sentence
                }
            }
        }
        
        return achievements;
    }

    /**
     * Check specificity vs generic language
     */
    checkSpecificity(resumeText) {
        const specificWords = ['implemented', 'developed', 'built', 'created', 'designed', 'optimized', 'reduced', 'increased'];
        const genericWords = ['responsible for', 'worked on', 'helped with', 'assisted', 'participated', 'involved in'];
        
        let specificCount = 0;
        let genericCount = 0;
        
        const lowerText = resumeText.toLowerCase();
        
        specificWords.forEach(word => {
            const matches = (lowerText.match(new RegExp(word, 'g')) || []).length;
            specificCount += matches;
        });
        
        genericWords.forEach(phrase => {
            const matches = (lowerText.match(new RegExp(phrase, 'g')) || []).length;
            genericCount += matches;
        });
        
        const totalWords = specificCount + genericCount;
        if (totalWords === 0) return 50; // No evidence either way
        
        const specificityRatio = specificCount / totalWords;
        return Math.round(specificityRatio * 100);
    }

    /**
     * Detect job hopping pattern
     */
    detectJobHopping(workHistory) {
        if (!workHistory || workHistory.length < 3) return false;
        
        const shortStints = workHistory.filter(job => {
            const duration = job.endYear - job.startYear;
            return duration < 1; // Less than 1 year
        });
        
        // If more than half of recent jobs are short stints
        return (shortStints.length / workHistory.length) > 0.5;
    }

    /**
     * Detect unrealistic skill claims
     */
    detectSkillInflation(skills) {
        if (!skills || skills.length === 0) return false;
        
        // Red flag: claiming expertise in too many unrelated technologies
        const frameworks = ['react', 'angular', 'vue', 'svelte'];
        const backends = ['node.js', 'python', 'java', 'php', 'ruby', 'go', 'rust'];
        const databases = ['mysql', 'postgresql', 'mongodb', 'redis', 'cassandra'];
        
        const frameworkCount = skills.filter(skill => 
            frameworks.some(fw => skill.toLowerCase().includes(fw))
        ).length;
        
        const backendCount = skills.filter(skill => 
            backends.some(be => skill.toLowerCase().includes(be))
        ).length;
        
        const dbCount = skills.filter(skill => 
            databases.some(db => skill.toLowerCase().includes(db))
        ).length;
        
        // Red flag if claiming expertise in too many competing technologies
        return frameworkCount > 3 || backendCount > 4 || dbCount > 4;
    }

    /**
     * Extract current/most recent job title
     */
    extractCurrentTitle(resumeText) {
        const lines = resumeText.split('\n');
        
        // Look for job titles (usually have company names or dates)
        for (const line of lines) {
            if (this.looksLikeJobTitle(line)) {
                return line.split(/[|\-@]/)[0].trim();
            }
        }
        
        return 'Unknown Title';
    }

    /**
     * Check if line looks like job title
     */
    looksLikeJobTitle(line) {
        const titleWords = ['engineer', 'developer', 'manager', 'analyst', 'designer', 'architect'];
        const lowerLine = line.toLowerCase();
        
        return titleWords.some(word => lowerLine.includes(word)) && 
               (line.includes('2023') || line.includes('2024') || line.includes('present'));
    }

    /**
     * Extract top skills (first 5-10 mentioned skills)
     */
    extractTopSkills(resumeText) {
        const skills = [];
        const skillPatterns = [
            /\b(javascript|typescript|python|java|react|angular|vue|node\.js)\b/gi,
            /\b(aws|azure|docker|kubernetes|git|sql|mongodb)\b/gi
        ];
        
        for (const pattern of skillPatterns) {
            const matches = resumeText.match(pattern) || [];
            skills.push(...matches.map(m => m.toLowerCase()));
        }
        
        return [...new Set(skills)].slice(0, 8); // Top 8 unique skills
    }

    /**
     * Generate human feedback
     */
    generateHumanFeedback(recognitionScore, credibilityScore, redFlagScore, resumeProfile, jobProfile) {
        const feedback = {
            willCall: recognitionScore > 70 && credibilityScore > 60 && redFlagScore > 70,
            recognitionIssues: [],
            credibilityIssues: [],
            redFlags: [],
            recommendations: []
        };
        
        // Recognition issues
        if (recognitionScore < 70) {
            feedback.recognitionIssues.push('Job title doesn\'t immediately align with target role');
            feedback.recommendations.push('Adjust job title to better match target role');
        }
        
        // Credibility issues
        if (credibilityScore < 60) {
            feedback.credibilityIssues.push('Lacks quantified achievements');
            feedback.recommendations.push('Add specific numbers, percentages, or dollar amounts to achievements');
        }
        
        // Red flag issues
        if (redFlagScore < 70) {
            feedback.redFlags.push('Potential concerns detected in work history or skill claims');
        }
        
        return feedback;
    }

    /**
     * Initialize helper data structures
     */
    initializeRedFlags() {
        return {
            jobHopping: ['less than 1 year', 'short tenure'],
            skillInflation: ['expert in everything', 'too many technologies'],
            buzzwords: ['synergy', 'thought leader', 'ninja', 'rockstar', 'guru']
        };
    }

    initializePositiveSignals() {
        return {
            progression: ['promoted', 'advanced', 'led team'],
            impact: ['increased', 'improved', 'reduced', 'optimized'],
            leadership: ['managed', 'led', 'mentored', 'supervised']
        };
    }

    initializeAchievementPatterns() {
        return [
            /increased\s+\w+\s+by\s+\d+%/gi,
            /reduced\s+\w+\s+by\s+\d+%/gi,
            /improved\s+\w+\s+by\s+\d+%/gi,
            /generated\s+\$[\d,]+/gi,
            /saved\s+\$[\d,]+/gi
        ];
    }

    /**
     * Stub helper methods (implement as needed)
     */
    extractRecentExperience(resumeText) {
        return 'Recent experience analysis'; // Simplified for now
    }
    
    extractWorkHistory(resumeText) {
        return []; // Simplified for now
    }
    
    extractAllSkills(resumeText) {
        return this.extractTopSkills(resumeText); // Reuse top skills for now
    }
    
    extractAchievements(resumeText) {
        return this.extractQuantifiedAchievements(resumeText);
    }
    
    extractTargetTitle(jobDescription) {
        const lines = jobDescription.split('\n');
        return lines[0] || 'Unknown Position'; // First line usually has title
    }
    
    extractRequiredSkills(jobDescription) {
        const skills = [];
        const skillPatterns = [
            /\b(javascript|typescript|python|java|react|angular|vue)\b/gi,
            /\b(aws|azure|docker|kubernetes|git|sql)\b/gi
        ];
        
        for (const pattern of skillPatterns) {
            const matches = jobDescription.match(pattern) || [];
            skills.push(...matches.map(m => m.toLowerCase()));
        }
        
        return [...new Set(skills)];
    }
    
    extractIndustry(jobDescription) {
        const text = jobDescription.toLowerCase();
        if (text.includes('fintech') || text.includes('financial')) return 'fintech';
        if (text.includes('healthcare') || text.includes('medical')) return 'healthcare';
        if (text.includes('ecommerce') || text.includes('retail')) return 'ecommerce';
        return 'technology';
    }
    
    extractSeniorityLevel(jobDescription) {
        const text = jobDescription.toLowerCase();
        if (text.includes('senior')) return 'senior';
        if (text.includes('junior')) return 'junior';
        if (text.includes('lead')) return 'lead';
        return 'mid';
    }
    
    checkExperienceRelevance(recentExperience, industry) {
        return 70; // Default score for now
    }
    
    assessTechnicalDepth(skills, resumeText) {
        return Math.min((skills.length / 5) * 100, 100); // Based on skill count for now
    }
    
    detectBuzzwordOverload(resumeText) {
        const buzzwords = ['synergy', 'leverage', 'utilize', 'guru', 'ninja', 'rockstar'];
        const text = resumeText.toLowerCase();
        let buzzwordCount = 0;
        
        buzzwords.forEach(buzzword => {
            if (text.includes(buzzword)) buzzwordCount++;
        });
        
        return buzzwordCount > 3; // More than 3 buzzwords = overload
    }

    /**
     * Fallback results when human scan fails
     */
    getFallbackHumanResults() {
        return {
            humanScore: 50,
            breakdown: {
                instantRecognition: 50,
                credibilityIndicators: 50,
                redFlagDetection: 70
            },
            details: {
                willCall: false,
                recognitionIssues: ['Unable to analyze recognition factors'],
                credibilityIssues: ['Unable to analyze credibility'],
                redFlags: [],
                recommendations: ['Try again with clearer resume content']
            },
            analysisTime: 0
        };
    }

    // Detect employment gaps >6 months and return penalty 0-5
    calculateGapPenalty(resumeText) {
        // naive date extraction MM YYYY or Mon YYYY
        const dateRegex = /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{4}/gi;
        const matches = (resumeText.match(dateRegex) || []).map(m => new Date(m));
        if (matches.length < 2) return 0;
        // sort desc
        matches.sort((a,b) => a - b);
        let maxGapMonths = 0;
        for(let i=1;i<matches.length;i++){
            const diffMonths = (matches[i] - matches[i-1])/(1000*60*60*24*30);
            if(diffMonths>maxGapMonths) maxGapMonths = diffMonths;
        }
        const gapPenalty = Math.min(Math.floor(maxGapMonths/6),5);
        return gapPenalty;
    }
}

export default HumanScanner; 